import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Alert, Placeholder, Dropdown } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { getPortfolio, getAccounts, createAccount, getAccount, updateAccount, updatePortfolio, getTradingStatistics, getLastUpdateTime } from '../services/api';
import AccountPortfolioTable from '../components/AccountPortfolioTable';
import ColumnVisibilityDropdown from '../components/ColumnVisibilityDropdown';
import PieChartModal from '../components/PieChartModal';
import BalanceHistoryModal from '../components/BalanceHistoryModal';
import PerformanceChartModal from '../components/PerformanceChartModal';
import { subscribeToTickerUpdates } from '../services/socketService';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { formatBalanceDisplay, normalizeNearZero } from '../utils/formatBalance';
import { createLogger } from '../utils/debugLogger';

const logger = createLogger(
  'portfolioDetailsPage',
  'Portfolio details page ticker refreshes and diagnostics',
  false,
  'DEBUG'
);

const MARKET_STATUS_STORAGE_KEY = 'allAccounts-marketClosed';

const readStoredMarketClosedStatus = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }
  try {
    return window.localStorage.getItem(MARKET_STATUS_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

const persistMarketClosedStatus = (isClosed) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(MARKET_STATUS_STORAGE_KEY, isClosed ? 'true' : 'false');
  } catch (error) {
    // Ignore storage problems; status will still update in memory
  }
};

// Custom styles for the component - same as AllAccounts
const customStyles = `
    /* Enhanced card styling for maximum contrast and prominence */
    .portfolio-details-card {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out !important;
    }
    
    .portfolio-details-card:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 8px 24px var(--shadow-color) !important;
    }
    
    [data-theme="light"] .portfolio-details-card {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="light"] .portfolio-details-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="dark"] .portfolio-details-card {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    
    [data-theme="dark"] .portfolio-details-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
    }
    
    /* Enhanced table container styling */
    .portfolio-table-container {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        overflow: hidden !important;
        margin-bottom: 1rem !important;
    }
    
    [data-theme="light"] .portfolio-table-container {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="dark"] .portfolio-table-container {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    
    /* Enhanced table styling */
    .portfolio-table-container .account-portfolio-table {
        background-color: var(--card-bg) !important;
        border: none !important;
        margin-bottom: 0 !important;
    }
    
    /* Remove gap between last row and bottom of table container */
    .portfolio-table-container .account-portfolio-table {
        margin-bottom: 0 !important;
    }
    
    /* Enhanced table header styling */
    .portfolio-table-container .account-portfolio-table thead th,
    .portfolio-table-container .portfolio-table-header {
        background-color: var(--bg-secondary) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        transition: background-color 0.2s ease-in-out;
    }
    
    .portfolio-table-container .account-portfolio-table thead th:hover,
    .portfolio-table-container .portfolio-table-header:hover {
        background-color: var(--hover-bg) !important;
        cursor: pointer;
    }
    
    [data-theme="light"] .portfolio-table-container .account-portfolio-table thead th,
    [data-theme="light"] .portfolio-table-container .portfolio-table-header {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .portfolio-table-container .account-portfolio-table thead th:hover,
    [data-theme="light"] .portfolio-table-container .portfolio-table-header:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .portfolio-table-container .account-portfolio-table thead th,
    [data-theme="dark"] .portfolio-table-container .portfolio-table-header {
        background-color: #374151 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .portfolio-table-container .account-portfolio-table thead th:hover,
    [data-theme="dark"] .portfolio-table-container .portfolio-table-header:hover {
        background-color: #4b5563 !important;
    }
    
    /* Enhanced table row styling with hover effects */
    .portfolio-table-container .account-portfolio-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        transition: all 0.2s ease-in-out !important;
    }
    
    .portfolio-table-container .account-portfolio-table tbody tr:hover {
        background-color: var(--hover-bg) !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
        cursor: pointer !important;
    }
    
    [data-theme="light"] .portfolio-table-container .account-portfolio-table tbody tr:hover {
        background-color: #f1f5f9 !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="dark"] .portfolio-table-container .account-portfolio-table tbody tr:hover {
        background-color: #4b5563 !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
    }
    
    /* Enhanced table cell styling - more compact */
    .portfolio-table-container .account-portfolio-table tbody td {
        padding: 4px 6px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
    }
    
    [data-theme="light"] .portfolio-table-container .account-portfolio-table tbody td {
        border-bottom: 1px solid #e5e7eb !important;
    }
    
    [data-theme="dark"] .portfolio-table-container .account-portfolio-table tbody td {
        border-bottom: 1px solid #374151 !important;
    }
    
    /* Enhanced total row styling - match header styling exactly */
    .portfolio-table-container .account-portfolio-table .table-total {
        background-color: var(--bg-secondary) !important;
        border-top: 2px solid var(--border-color) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
    }
    
    .portfolio-table-container .account-portfolio-table .table-total td {
        background-color: var(--bg-secondary) !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        border-bottom: none !important;
    }
    
    .portfolio-table-container .account-portfolio-table .table-total:hover {
        background-color: var(--hover-bg) !important;
        cursor: default !important;
        transform: none !important;
        box-shadow: none !important;
    }
    
    [data-theme="light"] .portfolio-table-container .account-portfolio-table .table-total {
        background-color: #f8f9fa !important;
        border-top: 2px solid #d1d5db !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .portfolio-table-container .account-portfolio-table .table-total td {
        background-color: #f8f9fa !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .portfolio-table-container .account-portfolio-table .table-total:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .portfolio-table-container .account-portfolio-table .table-total {
        background-color: #374151 !important;
        border-top: 2px solid #4b5563 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .portfolio-table-container .account-portfolio-table .table-total td {
        background-color: #374151 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .portfolio-table-container .account-portfolio-table .table-total:hover {
        background-color: #4b5563 !important;
    }
    
    /* CSS Variables for theme support */
    :root {
        --card-bg: #ffffff;
        --border-color: #d1d5db;
        --shadow-color: rgba(0, 0, 0, 0.1);
        --text-primary: #374151;
        --bg-secondary: #f8f9fa;
        --hover-bg: #f1f5f9;
    }
    
    [data-theme="dark"] {
        --card-bg: #1f2937;
        --border-color: #4b5563;
        --shadow-color: rgba(0, 0, 0, 0.3);
        --text-primary: #f9fafb;
        --bg-secondary: #374151;
        --hover-bg: #4b5563;
    }
`;

function PortfolioDetails() {
  const { portfolioId } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountDescription, setNewAccountDescription] = useState('');
  const [newAccountCash, setNewAccountCash] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [showPieChartModal, setShowPieChartModal] = useState(false);
  const [showBalanceHistoryModal, setShowBalanceHistoryModal] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [overallExpectancy, setOverallExpectancy] = useState(null);
  const marketClosedRef = useRef(readStoredMarketClosedStatus());
  const updateMarketClosedState = useCallback((nextValue) => {
    if (typeof nextValue !== 'boolean') {
      return false;
    }
    const previous = marketClosedRef.current;
    if (previous === nextValue) {
      return false;
    }
    marketClosedRef.current = nextValue;
    persistMarketClosedStatus(nextValue);
    return previous && !nextValue;
  }, []);
  
  // Edit account state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editType, setEditType] = useState('name'); // 'name' or 'description'
  const [editAccountId, setEditAccountId] = useState(null);
  const [editAccountValue, setEditAccountValue] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  
  // Edit portfolio state
  const [showEditPortfolioModal, setShowEditPortfolioModal] = useState(false);
  const [editPortfolioName, setEditPortfolioName] = useState('');
  const [editPortfolioDescription, setEditPortfolioDescription] = useState('');
  const [editPortfolioLoading, setEditPortfolioLoading] = useState(false);
  const [editPortfolioError, setEditPortfolioError] = useState('');

  // Default column order
  const defaultColumnOrder = [
    'portfolio',
    'description', 
    'balance',
    'cash',
    'today_gain_amount',
    'today_gain_percentage',
    'total_gain_amount',
    'total_gain_percentage',
    'ytd_gain_amount',
    'ytd_gain_percentage'
  ];
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const savedColumns = localStorage.getItem(`visibleColumns-portfolio-${portfolioId}`);
    if (savedColumns) {
      const parsed = JSON.parse(savedColumns);
      return {
        ...parsed,
        // Ensure these columns are always present if not in localStorage
        portfolio: parsed.portfolio !== undefined ? parsed.portfolio : true,
        balance: parsed.balance !== undefined ? parsed.balance : true,
        cash: parsed.cash !== undefined ? parsed.cash : true,
        today_gain_percentage: parsed.today_gain_percentage !== undefined ? parsed.today_gain_percentage : true,
        ytd_gain_percentage: parsed.ytd_gain_percentage !== undefined ? parsed.ytd_gain_percentage : true
      };
    }
    return {
      portfolio: true,
      description: true,
      balance: true,
      cash: true,
      today_gain_amount: true,
      today_gain_percentage: true,
      total_gain_amount: true,
      total_gain_percentage: true,
      ytd_gain_amount: true,
      ytd_gain_percentage: true
    };
  });
  
  // Column order state
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem(`columnOrder-portfolio-${portfolioId}`);
    return savedOrder ? JSON.parse(savedOrder) : defaultColumnOrder;
  });
  
  // Save column visibility settings when they change
  useEffect(() => {
    if (portfolioId) {
      localStorage.setItem(`visibleColumns-portfolio-${portfolioId}`, JSON.stringify(visibleColumns));
    }
  }, [visibleColumns, portfolioId]);
  
  // Save column order when it changes
  useEffect(() => {
    if (portfolioId) {
      localStorage.setItem(`columnOrder-portfolio-${portfolioId}`, JSON.stringify(columnOrder));
    }
  }, [columnOrder, portfolioId]);

  useEffect(() => {
    let cancelled = false;
    const initializeMarketStatus = async () => {
      try {
        const response = await getLastUpdateTime();
        if (!cancelled && response?.data && typeof response.data.market_closed === 'boolean') {
          updateMarketClosedState(response.data.market_closed);
        }
      } catch (error) {
        // Ignore initialization failures; later ticker events will update status
      }
    };
    initializeMarketStatus();
    return () => {
      cancelled = true;
    };
  }, [updateMarketClosedState]);

  const fetchPortfolioDetails = useCallback(async () => {
    setTableLoading(true);
    setError('');
    
    try {
      // Fetch portfolio details
      const portfolioResponse = await getPortfolio(portfolioId);
      logger.debug('Portfolio API response', portfolioResponse.data);
      setPortfolio(portfolioResponse.data);

      // Fetch accounts in this portfolio
      const accountsResponse = await getAccounts(portfolioId);
      logger.debug('Accounts API response', accountsResponse.data);
      setAccounts(accountsResponse.data);
    } catch (err) {
      logger.error('Error fetching portfolio details', err);
      setError('Failed to load portfolio details. Please try again.');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    fetchPortfolioDetails();
  }, [fetchPortfolioDetails]);

  // Keep in sync with global portfoliosUpdated events
  useEffect(() => {
    const handler = (event) => {
      const incoming = event?.detail?.portfolios;
      if (!incoming || !Array.isArray(incoming)) return;
      const matched = incoming.find(p => String(p.portfolio_id) === String(portfolioId));
      if (matched) {
        setPortfolio(matched);
        // If portfolio includes accounts in payload, prefer them; otherwise leave current accounts as-is
        if (Array.isArray(matched.accounts) && matched.accounts.length > 0) {
          setAccounts(matched.accounts);
        }
      }
    };
    window.addEventListener('portfoliosUpdated', handler);
    return () => window.removeEventListener('portfoliosUpdated', handler);
  }, [portfolioId]);

  // Load overall trading statistics (expectancy)
  useEffect(() => {
    const loadStats = async () => {
      try {
        const resp = await getTradingStatistics();
        setOverallExpectancy(typeof resp.data?.expectancy === 'number' ? resp.data.expectancy : null);
      } catch (e) {
        setOverallExpectancy(null);
      }
    };
    loadStats();
  }, []);

  // Add WebSocket subscription to automatically refresh account data when ticker prices change
  useEffect(() => {
    logger.debug('PortfolioDetails: Setting up ticker update subscription');

    const unsubscribe = subscribeToTickerUpdates((data) => {
      logger.debug('PortfolioDetails: Received ticker update via WebSocket', {
        hasUpdatedTickers: Boolean(data?.updated_tickers?.length > 0),
        updateCount: data?.updated_tickers?.length || 0,
        hasLastUpdateTime: Boolean(data?.latest_update_time),
        isTimestampEvent: data?.message === 'Timestamp update event'
      });
      
      const reopened = updateMarketClosedState(data?.market_closed);

      if (marketClosedRef.current) {
        return;
      }

      const hasTickerUpdates = Boolean(data && data.updated_tickers && data.updated_tickers.length > 0);
      if (reopened || hasTickerUpdates) {
        logger.debug(
          `PortfolioDetails: Refreshing account data due to ${reopened ? 'market reopening' : `${data.updated_tickers.length} ticker updates`}`
        );
        fetchPortfolioDetails();
      }
    });

    return () => {
      logger.debug('PortfolioDetails: Cleaning up ticker update subscription');
      unsubscribe();
    };
  }, [fetchPortfolioDetails, updateMarketClosedState]);

  // Compare account data from different sources
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      // Log the current account data from the accounts list
      logger.debug('Portfolio accounts list data', accounts);
      
      // For the first account, directly fetch its details for comparison
      const compareWithDetailPage = async () => {
        try {
          const firstAccountId = accounts[0].account_id;
          const accountResponse = await getAccount(firstAccountId);
          logger.debug('Direct account fetch data', accountResponse.data);
          logger.debug('Comparison - accounts list vs direct fetch', {
            listData: {
              today_gain_percentage: accounts[0].today_gain_percentage,
              total_gain_percentage: accounts[0].total_gain_percentage
            },
            directFetch: {
              today_gain_percentage: accountResponse.data.today_gain_percentage,
              total_gain_percentage: accountResponse.data.total_gain_percentage
            }
          });
        } catch (err) {
          logger.error('Error fetching account details for comparison', err);
        }
      };
      
      compareWithDetailPage();
    }
  }, [accounts]);

  // Calculate total values from accounts for consistency
  const accountTotals = useMemo(() => {
    if (!accounts || accounts.length === 0) {
      return {
        today_gain_amount: 0,
        today_gain_percentage: 0,
        total_gain_amount: 0,
        total_gain_percentage: 0,
        bottom_line: 0
      };
    }
    
    const total_today_gain = accounts.reduce((sum, account) => sum + account.today_gain_amount, 0);
    const total_balance = accounts.reduce((sum, account) => sum + account.balance, 0);
    const total_cash = accounts.reduce((sum, account) => sum + (account.cash || 0), 0);
    const total_gain_amount = accounts.reduce((sum, account) => sum + (account.total_gain_amount || 0), 0);
    const total_bottom_line = accounts.reduce((sum, account) => sum + (account.bottom_line || 0), 0);
    
    // Check if this portfolio is mostly cash
    const cash_percentage = total_balance > 0 ? total_cash / total_balance : 0;
    const all_previous_day_balances = accounts.reduce((sum, account) => sum + (account.previous_day_balance || 0), 0);
    
    // For mostly cash portfolios with no previous day balance, adjust the today's gain
    let adjusted_today_gain = total_today_gain;
    if (all_previous_day_balances === 0 && cash_percentage > 0.9) {
      adjusted_today_gain = 0;
    }
    
    // Calculate percentages. Denominator is the previous day's balance, not
    // today's -- matching the backend's own today_gain_percentage formula
    // (accounts.py) and how AllAccounts.js/Dashboard.js aggregate the same
    // metric. Dividing by total_balance (today's, i.e. previous + gain)
    // understates the percentage on big move days: e.g. a $10k gain on a
    // $100k previous balance is a real +10%, but $10k / $110k (today's
    // balance) reports it as +9.09%.
    const today_gain_percentage = all_previous_day_balances > 0 ? (adjusted_today_gain / all_previous_day_balances) * 100 : 0;
    
    // Calculate ticker cost basis (excluding cash)
    const total_ticker_cost_basis = accounts.reduce((sum, account) => {
      const account_cost_basis = account.balance - (account.total_gain_amount || 0);
      return sum + (account_cost_basis > 0 ? account_cost_basis : 0);
    }, 0);
    
    const total_gain_percentage = total_ticker_cost_basis > 0 ? (total_gain_amount / total_ticker_cost_basis) * 100 : 0;
    
    return {
      today_gain_amount: adjusted_today_gain,
      today_gain_percentage: today_gain_percentage,
      total_gain_amount: total_gain_amount,
      total_gain_percentage: total_gain_percentage,
      bottom_line: total_bottom_line
    };
  }, [accounts]);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    if (!newAccountName.trim()) {
      setCreateError('Account name is required');
      return;
    }
    
    if (isNaN(parseFloat(newAccountCash)) || parseFloat(newAccountCash) < 0) {
      setCreateError('Initial cash must be a valid number greater than or equal to 0');
      return;
    }
    
    setCreateError('');
    setCreateLoading(true);
    
    try {
      await createAccount({
        portfolio_id: portfolioId,
        name: newAccountName.trim(),
        description: newAccountDescription.trim(),
        balance: parseFloat(newAccountCash),
        cash: parseFloat(newAccountCash)
      });
      
      // Refresh the accounts list
      await fetchPortfolioDetails();
      
      // Reset form and close modal
      setNewAccountName('');
      setNewAccountDescription('');
      setNewAccountCash('');
      setShowCreateModal(false);
    } catch (err) {
      logger.error('Error creating account', err);
      
      if (err.response && err.response.data && err.response.data.error) {
        setCreateError(err.response.data.error);
      } else {
        setCreateError('Failed to create account. Please try again.');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  // Function to run diagnostic data collection
  // Edit functions
  const handleEditName = (account) => {
    setEditType('name');
    setEditAccountId(account.account_id);
    setEditAccountValue(account.name);
    setShowEditModal(true);
  };
  
  const handleEditDescription = (account) => {
    setEditType('description');
    setEditAccountId(account.account_id);
    setEditAccountValue(account.description || '');
    setShowEditModal(true);
  };
  
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    
    if (editType === 'name' && !editAccountValue.trim()) {
      setEditError('Account name is required');
      return;
    }
    
    setEditError('');
    setEditLoading(true);
    
    try {
      // Prepare update data
      const updateData = {
        account_id: editAccountId
      };
      
      if (editType === 'name') {
        updateData.name = editAccountValue.trim();
      } else {
        updateData.description = editAccountValue.trim();
      }
      
      // Update account
      await updateAccount(updateData);
      
      // Refresh the accounts list
      await fetchPortfolioDetails();
      
      // Close modal
      setShowEditModal(false);
    } catch (err) {
      logger.error('Error updating account', err);
      
      if (err.response && err.response.data && err.response.data.error) {
        setEditError(err.response.data.error);
      } else {
        setEditError('Failed to update account. Please try again.');
      }
    } finally {
      setEditLoading(false);
    }
  };

  // Edit portfolio name
  const handleEditPortfolioName = () => {
    setEditPortfolioName(portfolio?.name || '');
    setEditPortfolioDescription(portfolio?.description || '');
    setShowEditPortfolioModal(true);
  };
  
  const handleSavePortfolioName = async (e) => {
    e.preventDefault();
    
    if (!editPortfolioName.trim()) {
      setEditPortfolioError('Portfolio name is required');
      return;
    }
    
    setEditPortfolioError('');
    setEditPortfolioLoading(true);
    
    try {
      // Update portfolio with new name
      await updatePortfolio(portfolioId, {
        name: editPortfolioName.trim(),
        description: editPortfolioDescription.trim()
      });
      
      // Refresh portfolio data
      const portfolioResponse = await getPortfolio(portfolioId);
      setPortfolio(portfolioResponse.data);
      
      // Close modal
      setShowEditPortfolioModal(false);
      
      // Dispatch event to notify other components (like NavigationPanel)
      window.dispatchEvent(new CustomEvent('nameChanged'));
    } catch (err) {
      logger.error('Error updating portfolio', err);
      
      if (err.response && err.response.data && err.response.data.error) {
        setEditPortfolioError(err.response.data.error);
      } else {
        setEditPortfolioError('Failed to update portfolio. Please try again.');
      }
    } finally {
      setEditPortfolioLoading(false);
    }
  };

  // Memoize filtered accounts to prevent unnecessary re-renders
  const filteredAccounts = useMemo(() => {
    return accounts.filter(account => account.balance > 0);
  }, [accounts]);

  if (error) {
    return (
      <Container className="mt-3">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-3">
      <style>{customStyles}</style>
      {error && (
        <Alert variant="danger">{error}</Alert>
      )}

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <h3 className="mb-0">
                {loading ? (
                  <Placeholder animation="glow">
                    <Placeholder xs={6} />
                  </Placeholder>
                ) : (
                  <>
                    {portfolio?.name}
                    <span 
                      className="ms-2 fs-6" 
                      style={{ cursor: 'pointer' }}
                      onClick={handleEditPortfolioName}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </span>
                  </>
                )}
              </h3>
              {loading ? (
                <Placeholder animation="glow" className="ms-3">
                  <Placeholder xs={8} />
                </Placeholder>
              ) : (
                <p className="text-muted mb-0 ms-3">{portfolio?.description}</p>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Balance and Gain Cards */}
      {(!loading && accounts.length > 0) || loading ? (
        <Row className="mb-4">
          <Col className="d-flex flex-wrap">
            <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
              <Card bg="white" className="h-100">
                <Card.Body className="p-2 text-center">
                  <Card.Title className="fs-6 mb-1">Total Balance</Card.Title>
                  <div className="text-muted small" style={{height: "18px"}}></div>
                  {loading ? (
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={8} className="fs-5" />
                    </Placeholder>
                  ) : (
                    <Card.Text className="fs-5 mb-0 fw-bold">
                      {formatBalanceDisplay(accounts.reduce((sum, account) => sum + account.balance, 0))}
                    </Card.Text>
                  )}
                  <div style={{height: "19px"}}></div>
                </Card.Body>
              </Card>
            </div>
            {/* Bottom Line Card - 2nd position */}
            <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
              <Card bg="white" className="h-100">
                <Card.Body className="p-2 text-center">
                  <Card.Title className="fs-6 mb-1">
                    Bottom Line
                    <i 
                      className="bi bi-info-circle ms-1" 
                      style={{ fontSize: '0.8rem', cursor: 'help' }}
                      title="Bottom line risks calculate with current account value. If you see bigger negative risks, consider raising your stop loss to reduce bottom line risks."
                    ></i>
                  </Card.Title>
                  <div className="text-muted small" style={{height: "18px"}}>If all stop losses hit</div>
                  {loading ? (
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={8} className="fs-5" />
                    </Placeholder>
                  ) : (
                    <>
                      <Card.Text className="fs-5 mb-0 fw-bold">
                        {formatBalanceDisplay(accountTotals?.bottom_line)}
                      </Card.Text>
                      <span className="d-block small text-muted">
                        ({accounts.reduce((sum, account) => sum + account.balance, 0) > 0 && accountTotals?.bottom_line !== undefined ? ((accountTotals.bottom_line / accounts.reduce((sum, account) => sum + account.balance, 0)) * 100).toFixed(2) : '0.00'}%)
                        {accounts.reduce((sum, account) => sum + account.balance, 0) > 0 && accountTotals?.bottom_line !== undefined && (
                          <span
                            style={{
                              // Same -3%-threshold coloring as the equivalent
                              // Bottom Line card on the All Accounts page
                              // (Dashboard.js) -- this used to hardcode red
                              // unconditionally, so a -0.1% risk and a -50%
                              // risk rendered identically alarming here.
                              color: (() => {
                                const totalBalance = accounts.reduce((sum, account) => sum + account.balance, 0);
                                const pct = (accountTotals.bottom_line / totalBalance) * 100;
                                const effect = pct - 100;
                                if (effect <= -3) return 'red';
                                if (effect > -3) return 'goldenrod';
                                return 'inherit';
                              })(),
                              marginLeft: 6,
                            }}
                          >
                            ({(() => {
                              const totalBalance = accounts.reduce((sum, account) => sum + account.balance, 0);
                              const pct = ((accountTotals.bottom_line / totalBalance) * 100);
                              const effect = pct - 100;
                              return `${effect >= 0 ? '+' : ''}${effect.toFixed(2)}%`;
                            })()})
                          </span>
                        )}
                      </span>
                    </>
                  )}
                  <div style={{height: "19px"}}></div>
                </Card.Body>
              </Card>
            </div>
            {/* Cash Balance Card - now 3rd */}
            <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
              <Card bg="white" className="h-100">
                <Card.Body className="p-2 text-center">
                  <Card.Title className="fs-6 mb-1">Cash Balance</Card.Title>
                  <div className="text-muted small" style={{height: "18px"}}></div>
                  {loading ? (
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={8} className="fs-5" />
                    </Placeholder>
                  ) : (
                    <Card.Text className="fs-5 mb-0 fw-bold">
                      {formatBalanceDisplay(accounts.reduce((sum, account) => sum + (account.cash || 0), 0))}
                    </Card.Text>
                  )}
                  {loading ? (
                    <div style={{height: "19px"}}></div>
                  ) : (
                    <span className="d-block small text-muted">
                      ({accounts.reduce((sum, account) => sum + account.balance, 0) > 0 ? 
                        (accounts.reduce((sum, account) => sum + (account.cash || 0), 0) / accounts.reduce((sum, account) => sum + account.balance, 0) * 100).toFixed(1) : 0}%)
                    </span>
                  )}
                </Card.Body>
              </Card>
            </div>
            <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
              <Card bg="white" className="h-100">
                <Card.Body className="p-2 text-center">
                  <Card.Title className="fs-6 mb-1">Trading Edge</Card.Title>
                  <div className="text-muted small" style={{height: "18px"}}>Expectancy</div>
                  {loading ? (
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={6} className="fs-5" />
                    </Placeholder>
                  ) : (
                    <Card.Text className={`fs-5 mb-0 fw-bold text-${(overallExpectancy || 0) >= 0 ? 'success' : 'danger'}`}>
                      {overallExpectancy != null ? `${overallExpectancy.toFixed(2)}%` : '-'}
                    </Card.Text>
                  )}
                  {!loading && (
                    <small className={`d-block small text-${(overallExpectancy || 0) >= 0 ? 'success' : 'danger'}`}>
                      {(overallExpectancy || 0) >= 0 ? 'Profitable Strategy' : 'Losing Strategy'}
                    </small>
                  )}
                </Card.Body>
              </Card>
            </div>
            <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
              <Card 
                bg="white"
                className="h-100"
              >
                <Card.Body className="p-2 text-center">
                  <Card.Title className="fs-6 mb-1">Today's Gain</Card.Title>
                  {loading ? (
                    <div className="text-muted small" style={{height: "18px"}}></div>
                  ) : (
                    <small className="text-muted d-block">prev bal basis</small>
                  )}
                  {loading ? (
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={7} className="fs-5" />
                    </Placeholder>
                  ) : (
                    (() => {
                      const amount = normalizeNearZero(accountTotals.today_gain_amount || 0);
                      return (
                        <Card.Text className={`fs-5 mb-0 fw-bold text-${amount >= 0 ? 'success' : 'danger'}`}>
                          {formatBalanceDisplay(amount)}
                        </Card.Text>
                      );
                    })()
                  )}
                  {loading ? (
                    <div style={{height: "19px"}}></div>
                  ) : (
                    (() => {
                      const pct = normalizeNearZero(accountTotals.today_gain_percentage || 0);
                      return (
                        <span className={`d-block small text-${pct >= 0 ? 'success' : 'danger'}`}>
                          ({Math.abs(pct).toFixed(2)}%)
                        </span>
                      );
                    })()
                  )}
                </Card.Body>
              </Card>
            </div>
            <div className="col-xl col-lg-6 col-md-6 col-sm-6 pb-3 px-1">
              <Card 
                bg="white"
                className="h-100"
              >
                <Card.Body className="p-2 text-center">
                  <Card.Title className="fs-6 mb-1">Total Gain</Card.Title>
                  <div className="text-muted small" style={{height: "18px"}}></div>
                  {loading ? (
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={7} className="fs-5" />
                    </Placeholder>
                  ) : (
                    <Card.Text className={`fs-5 mb-0 fw-bold text-${accountTotals.total_gain_amount >= 0 ? 'success' : 'danger'}`}>
                      {formatBalanceDisplay(accountTotals.total_gain_amount)}
                    </Card.Text>
                  )}
                  {loading ? (
                    <div style={{height: "19px"}}></div>
                  ) : (
                    <span className={`d-block small text-${accountTotals.total_gain_amount >= 0 ? 'success' : 'danger'}`}>
                      ({accountTotals.total_gain_percentage.toFixed(2)}%)
                    </span>
                  )}
                </Card.Body>
              </Card>
            </div>
            <div className="col-xl col-lg-6 col-md-6 col-sm-6 pb-3 px-1">
              <Card 
                bg="white"
                className="h-100"
              >
                <Card.Body className="p-2 text-center">
                  <Card.Title className="fs-6 mb-1">Year-to-Date</Card.Title>
                  {loading ? (
                    <div className="text-muted small" style={{height: "18px"}}></div>
                  ) : (
                    accounts.some(account => account.ytd_start_date) ? (
                      <small className="text-muted d-block">since {new Date(accounts.filter(account => account.ytd_start_date)[0].ytd_start_date).toLocaleDateString()}</small>
                    ) : (
                      <div className="text-muted small" style={{height: "18px"}}></div>
                    )
                  )}
                  {loading ? (
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={7} className="fs-5" />
                    </Placeholder>
                  ) : (
                    <Card.Text className={`fs-5 mb-0 fw-bold text-${accounts.reduce((sum, account) => sum + (account.ytd_gain_amount || 0), 0) >= 0 ? 'success' : 'danger'}`}>
                      {formatBalanceDisplay(accounts.reduce((sum, account) => sum + (account.ytd_gain_amount || 0), 0))}
                    </Card.Text>
                  )}
                  {loading ? (
                    <div style={{height: "19px"}}></div>
                  ) : (
                    <span className={`d-block small text-${(portfolio?.ytd_gain_percentage || 0) >= 0 ? 'success' : 'danger'}`}>
                      ({(portfolio?.ytd_gain_percentage || 0).toFixed(2)}%)
                    </span>
                  )}
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      ) : null}

      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              {!loading && accounts.length > 0 && (
                <>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowBalanceHistoryModal(true)}
                    size="sm"
                    className="me-2"
                  >
                    <i className="bi bi-graph-up"></i> Balance
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowPerformanceModal(true)}
                    size="sm"
                    className="me-2"
                  >
                    <i className="bi bi-speedometer2"></i> Performance
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowPieChartModal(true)}
                    size="sm"
                    className="me-2"
                  >
                    <i className="bi bi-pie-chart"></i> Pie Chart
                  </Button>
                  <Button 
                    variant="outline-primary"
                    onClick={() => setShowCreateModal(true)}
                    size="sm"
                  >
                    <i className="bi bi-plus"></i> Account
                  </Button>
                </>
              )}
            </div>
            <div>
              {/* Column configuration dropdown */}
              {visibleColumns && (
                <ColumnVisibilityDropdown
                  visibleColumns={visibleColumns}
                  columns={Object.keys(visibleColumns)}
                  onToggleColumn={(column, newValue) => {
                    setVisibleColumns(prev => ({
                      ...prev,
                      [column]: newValue
                    }));
                  }}
                  idPrefix="header-column-toggle"
                  disabled={loading}
                >
                  <Dropdown.Divider />
                  <div className="text-muted small">
                    <div>• Click column headers to sort</div>
                  </div>
                  <Dropdown.Divider />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="w-100"
                    onClick={() => {
                      // Reset to defaults
                      setColumnOrder(defaultColumnOrder);
                      setVisibleColumns({
                        portfolio: true,
                        description: true,
                        balance: true,
                        cash: true,
                        today_gain_amount: true,
                        today_gain_percentage: true,
                        total_gain_amount: true,
                        total_gain_percentage: true,
                        ytd_gain_amount: true,
                        ytd_gain_percentage: true
                      });
                      // Clear localStorage
                      localStorage.removeItem(`columnOrder-portfolio-${portfolioId}`);
                      localStorage.removeItem(`visibleColumns-portfolio-${portfolioId}`);
                    }}
                  >
                    Reset Columns
                  </Button>
                </ColumnVisibilityDropdown>
              )}
            </div>
          </div>
        </Col>
      </Row>
      
      <div className="portfolio-table-container">
        <div className="table-responsive">
          <AccountPortfolioTable 
            items={accounts} 
            type="account" 
            showTotal={true}
            totalData={{
              name: "Total",
              balance: portfolio?.balance || 0,
              cash: portfolio?.cash || 0,
              today_gain_amount: accountTotals.today_gain_amount,
              today_gain_percentage: accountTotals.today_gain_percentage,
              total_gain_amount: accountTotals.total_gain_amount,
              total_gain_percentage: accountTotals.total_gain_percentage,
              ytd_gain_amount: portfolio?.ytd_gain_amount || 0,
              ytd_gain_percentage: portfolio?.ytd_gain_percentage || 0
            }}
            onEditName={handleEditName}
            onEditDescription={handleEditDescription}
            tableLoading={tableLoading}
            visibleColumns={visibleColumns}
            columnOrder={columnOrder}
          />
        </div>
      </div>
      
      {!tableLoading && accounts.length === 0 && (
        <div className="text-center my-5">
          <p className="mb-4">You don't have any accounts in this portfolio yet.</p>
          <Button 
            variant="outline-primary"
            onClick={() => setShowCreateModal(true)}
            size="sm"
          >
            <i className="bi bi-plus"></i> Account
          </Button>
        </div>
      )}
      
      {/* Pie Chart Modal */}
      <PieChartModal
        show={showPieChartModal}
        onHide={() => setShowPieChartModal(false)}
        portfolios={filteredAccounts}
        cash={0}
        title={portfolio ? `${portfolio.name} - Accounts Distribution` : 'Accounts Distribution'}
      />

      {/* Balance History Modal */}
      <BalanceHistoryModal
        show={showBalanceHistoryModal}
        onHide={() => setShowBalanceHistoryModal(false)}
        isAggregated={true}
        portfolioName={portfolio?.name}
        currentBalance={accounts?.reduce((sum, account) => sum + account.balance, 0) || null}
        isBalanceLoading={tableLoading}
      />
      <PerformanceChartModal
        show={showPerformanceModal}
        onHide={() => setShowPerformanceModal(false)}
        isAggregated={true}
        portfolioName={portfolio?.name}
        currentBalance={accounts?.reduce((sum, account) => sum + account.balance, 0) || null}
        isBalanceLoading={tableLoading}
      />

      {/* Create Account Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createError && (
            <Alert variant="danger">{createError}</Alert>
          )}
          <Form onSubmit={handleCreateAccount}>
            <Form.Group className="mb-3" controlId="accountName">
              <Form.Label>Account Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter account name"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="accountDescription">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter account description"
                value={newAccountDescription}
                onChange={(e) => setNewAccountDescription(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="accountCash">
              <Form.Label>Initial Total Balance (cash + stocks)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                placeholder="Enter initial total balance"
                value={newAccountCash}
                onChange={(e) => setNewAccountCash(e.target.value)}
                required
              />
            </Form.Group>
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit"
                disabled={createLoading}
              >
                {createLoading ? 'Creating...' : 'Create Account'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Account Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Account {editType === 'name' ? 'Name' : 'Description'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && (
            <Alert variant="danger">{editError}</Alert>
          )}
          <Form onSubmit={handleSaveEdit}>
            <Form.Group className="mb-3" controlId="editAccountField">
              <Form.Label>
                {editType === 'name' ? 'Account Name' : 'Description'}
              </Form.Label>
              {editType === 'name' ? (
                <Form.Control
                  type="text"
                  placeholder="Enter account name"
                  value={editAccountValue}
                  onChange={(e) => setEditAccountValue(e.target.value)}
                  required
                />
              ) : (
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter account description"
                  value={editAccountValue}
                  onChange={(e) => setEditAccountValue(e.target.value)}
                />
              )}
            </Form.Group>
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit"
                disabled={editLoading}
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Portfolio Name Modal */}
      <Modal show={showEditPortfolioModal} onHide={() => setShowEditPortfolioModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Portfolio Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editPortfolioError && (
            <Alert variant="danger">{editPortfolioError}</Alert>
          )}
          <Form onSubmit={handleSavePortfolioName}>
            <Form.Group className="mb-3" controlId="editPortfolioName">
              <Form.Label>Portfolio Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter portfolio name"
                value={editPortfolioName}
                onChange={(e) => setEditPortfolioName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="editPortfolioDescription">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter portfolio description"
                value={editPortfolioDescription}
                onChange={(e) => setEditPortfolioDescription(e.target.value)}
              />
            </Form.Group>
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit"
                disabled={editPortfolioLoading}
              >
                {editPortfolioLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default PortfolioDetails; 
